import React from 'react';
import {
  Database,
  Mail,
  FileText,
  Send,
  Bell,
  ClipboardList,
} from 'lucide-react';
import { Action, Subject } from '@erp/authz';
import { AgentTool, ToolCategory, isAllowableAgentPermission } from './types';

/**
 * Mock tools data for Agent Builder
 * Includes 11 tools across 3 categories with proper permission validation
 */

// Data Access Tools
const queryCustomers: AgentTool = {
  id: 'tool-query-customers',
  name: 'Query Customers',
  description: 'Search and retrieve customer information from the database',
  category: 'Data Access',
  requiredPermissions: [{ action: Action.Read, subject: Subject.Customer }],
};

const queryOrders: AgentTool = {
  id: 'tool-query-orders',
  name: 'Query Orders',
  description: 'Retrieve sales order details and history',
  category: 'Data Access',
  requiredPermissions: [{ action: Action.Read, subject: Subject.SalesOrder }],
};

const queryProducts: AgentTool = {
  id: 'tool-query-products',
  name: 'Query Products',
  description: 'Look up product information and specifications',
  category: 'Data Access',
  requiredPermissions: [{ action: Action.Read, subject: Subject.Product }],
};

const queryInventory: AgentTool = {
  id: 'tool-query-inventory',
  name: 'Query Inventory',
  description: 'Check inventory levels and warehouse stock',
  category: 'Data Access',
  requiredPermissions: [{ action: Action.Read, subject: Subject.Inventory }],
};

// Communication Tools
const sendEmail: AgentTool = {
  id: 'tool-send-email',
  name: 'Send Email',
  description: 'Send email notifications to customers and contacts',
  category: 'Communication',
  requiredPermissions: [],
};

const sendNotification: AgentTool = {
  id: 'tool-send-notification',
  name: 'Send Notification',
  description: 'Send in-app notifications to users',
  category: 'Communication',
  requiredPermissions: [],
};

const createTask: AgentTool = {
  id: 'tool-create-task',
  name: 'Create Task',
  description: 'Create and assign tasks for team members',
  category: 'Communication',
  requiredPermissions: [],
};

const logActivity: AgentTool = {
  id: 'tool-log-activity',
  name: 'Log Activity',
  description: 'Record activities and interactions in the system',
  category: 'Communication',
  requiredPermissions: [],
};

// Document Tools
const generateReport: AgentTool = {
  id: 'tool-generate-report',
  name: 'Generate Report',
  description: 'Create business reports and analytics',
  category: 'Document',
  requiredPermissions: [],
};

const exportCsv: AgentTool = {
  id: 'tool-export-csv',
  name: 'Export CSV',
  description: 'Export data to CSV format for analysis',
  category: 'Document',
  requiredPermissions: [],
};

const generateInvoice: AgentTool = {
  id: 'tool-generate-invoice',
  name: 'Generate Invoice',
  description: 'Create and format invoices for customers',
  category: 'Document',
  requiredPermissions: [{ action: Action.Read, subject: Subject.ARInvoice }],
};

/**
 * All available tools for the Agent Builder
 */
export const MOCK_TOOLS: AgentTool[] = [
  // Data Access
  queryCustomers,
  queryOrders,
  queryProducts,
  queryInventory,
  // Communication
  sendEmail,
  sendNotification,
  createTask,
  logActivity,
  // Document
  generateReport,
  exportCsv,
  generateInvoice,
];

/**
 * Tool categories
 */
export const TOOL_CATEGORIES: ToolCategory[] = [
  'Data Access',
  'Communication',
  'Document',
];

/**
 * Icons for each tool category
 */
export const categoryIcons: Record<ToolCategory, React.ReactNode> = {
  'Data Access': React.createElement(Database, { size: 20 }),
  Communication: React.createElement(Mail, { size: 20 }),
  Document: React.createElement(FileText, { size: 20 }),
};

/**
 * Tailwind color classes for each tool category
 */
export const categoryColors: Record<ToolCategory, string> = {
  'Data Access': 'bg-blue-100 text-blue-700',
  Communication: 'bg-purple-100 text-purple-700',
  Document: 'bg-amber-100 text-amber-700',
};

// Runtime validation in development
if (import.meta.env.DEV) {
  MOCK_TOOLS.forEach((tool) => {
    tool.requiredPermissions.forEach((perm) => {
      if (!isAllowableAgentPermission(perm.action, perm.subject)) {
        throw new Error(
          `Mock tool "${tool.name}" has disallowed permission: ${perm.action}:${perm.subject}. ` +
            `Only CRUD actions on non-payment subjects are allowed.`,
        );
      }
    });
  });
}
